import { z } from 'zod';
import type { Product } from './products';

export const runStatusSchema = z.enum(['queued', 'running', 'complete', 'failed', 'timeout']);
export type RunStatus = z.infer<typeof runStatusSchema>;
export const ACTIVE_RUN_STATUSES: readonly RunStatus[] = ['queued', 'running'];

/** Which agent-harness workflow a run executes in the sandbox: `dummy` for a title project, `script-entities` for a script. */
export const RUN_WORKFLOWS = ['dummy', 'script-entities'] as const;
export const runWorkflowSchema = z.enum(RUN_WORKFLOWS);
export type RunWorkflow = z.infer<typeof runWorkflowSchema>;

/** The workflow a project's runs execute, from its product: a script gets its entities found, a title the dummy. */
export function workflowForProduct(product: Product): RunWorkflow {
  return product === 'script' ? 'script-entities' : 'dummy';
}

/**
 * A row of public.runs. `created_by` is null for a run the worker started itself at placement; `script_version_id` is
 * the script version a `script-entities` run works on (null for `dummy`).
 */
export const runSchema = z.object({
  id: z.uuid(),
  project_id: z.uuid(),
  created_by: z.uuid().nullable(),
  status: runStatusSchema,
  workflow: runWorkflowSchema,
  script_version_id: z.uuid().nullable(),
  retry_of: z.uuid().nullable(),
  error: z.string().nullable(),
  summary_path: z.string().nullable(),
  correlation_id: z.string(),
  created_at: z.iso.datetime({ offset: true }),
  started_at: z.iso.datetime({ offset: true }).nullable(),
  finished_at: z.iso.datetime({ offset: true }).nullable(),
});
export type Run = z.infer<typeof runSchema>;

/** True while a run still occupies the project's single active slot. */
export function isActiveRun(run: Pick<Run, 'status'>): boolean {
  return ACTIVE_RUN_STATUSES.includes(run.status);
}

/** POST /api/projects/:projectId/runs body. runId is the client's idempotency key. */
export const createRunRequestSchema = z.object({
  runId: z.uuid(),
  retryOf: z.uuid().optional(),
});
export type CreateRunRequest = z.infer<typeof createRunRequestSchema>;

export const createRunResponseSchema = z.object({ run: runSchema });
export type CreateRunResponse = z.infer<typeof createRunResponseSchema>;
